/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { PERMISSIONS } from '@squiz/dxp-auth';
import { Button, ButtonProps, ICON_ADD_CIRCLE } from '@squiz/sds';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import { hasPermission } from 'apps/console-core/src/Utilities/HasPermission/HasPermission';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CDP_STRINGS } from '../../../../Constants/Strings.constants';
import { SEGMENTATION_DATA_IDS } from '../../Segmentation.constants';

/**
 * The create segment button.
 * @export
 * @returns {JSX.Element} The `<CreateSegmentButton />` component.
 */
export const CreateSegmentButton = (): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const createButtonRef = useRef<HTMLButtonElement>(null);

  const navigateToBuilder = useCallback((): void => {
    navigate({ pathname: getCapabilityPath(`/segmentation/create`) });
  }, [navigate]);

  // Permissions
  const canCreateSegment = hasPermission(PERMISSIONS.dxpSegmentation.SEGMENT_CREATE);

  // Labels
  const labels = useMemo(() => {
    return {
      createButton: t(CDP_STRINGS.createSegment),
    };
  }, [t]);

  const createButtonProps: ButtonProps = {
    classes: `create-segment__button`,
    icon: ICON_ADD_CIRCLE,
    isDisabled: !canCreateSegment,
    onClick: navigateToBuilder,
    text: labels.createButton,
  };

  return <Button {...createButtonProps} data-id={SEGMENTATION_DATA_IDS.createSegment} ref={createButtonRef} />;
};
